import { get } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

// Commons
import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { formatTextNumberInputValue } from '@commons/utils/format';
import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { translateUnit } from '@commons/utils/translateUnit';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { Input } from '@lib/inpulse/Input';

import {
  CONSUMPTION_FREQUENCY,
  getNonSalesConsumptionFrequencies,
} from '@admin/commons/constants/nonSalesConsumption';

import { Container, RowContainer } from './styledComponents';

export const useNonSalesConsumptionModal = () => {
  const [consumptionFrequency, setConsumptionFrequency] = useState(CONSUMPTION_FREQUENCY.DAILY);
  const [consumptionQuantity, setConsumptionQuantity] = useState(null);
  const [nonSalesConsumptionStores, setNonSalesConsumptionStores] = useState([]);

  return {
    consumptionFrequency,
    setConsumptionFrequency,
    consumptionQuantity,
    setConsumptionQuantity,
    nonSalesConsumptionStores,
    setNonSalesConsumptionStores,
  };
};

const ModalContent = ({
  consumptionFrequency,
  setConsumptionFrequency,
  consumptionQuantity,
  setConsumptionQuantity,
  nonSalesConsumptionStores,
  setNonSalesConsumptionStores,
  associatedStores,
  storeName,
  ingredientUnit,
  currency,
}) => {
  const frequencies = getNonSalesConsumptionFrequencies(currency);

  const isDailyConsumption = consumptionFrequency === CONSUMPTION_FREQUENCY.DAILY;

  const selectedFrequency = consumptionFrequency
    ? {
        id: isDailyConsumption
          ? CONSUMPTION_FREQUENCY.DAILY
          : CONSUMPTION_FREQUENCY.PER_1000_CURRENCY,
        value: isDailyConsumption
          ? i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_DAILY')
          : i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_PER_1000_CURRENCY', {
              currency,
            }),
      }
    : null;

  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [storesToDisplay, setStoresToDisplay] = useState(associatedStores);

  useEffect(() => {
    const allgroupsNamesSet = associatedStores.reduce((acc, { groups }) => {
      groups.forEach(({ name }) => {
        acc.add(name);
      });

      return acc;
    }, new Set());

    const allgroupsNames = [...allgroupsNamesSet];

    if (!allgroupsNames.length) {
      setGroups([]);
      setSelectedGroups([]);
      return;
    }

    allgroupsNames.push(i18next.t('GENERAL.SELECT_NONE_MASCULINE'));

    const allGroups = allgroupsNames.map((name, index) => ({ id: index, name }));

    setGroups(allGroups);
    setSelectedGroups(allGroups);
  }, []);

  useEffect(() => {
    if (!groups.length) {
      setStoresToDisplay(associatedStores);
      setNonSalesConsumptionStores(associatedStores);
      return;
    }

    const filteredStores = associatedStores.filter(({ groups: storeGroups }) => {
      if (!!storeGroups.length) {
        return storeGroups.some(({ name }) =>
          selectedGroups.some(({ name: selectedName }) => selectedName === name),
        );
      }

      return selectedGroups.some(({ name }) => name === i18next.t('GENERAL.SELECT_NONE_MASCULINE'));
    });

    setStoresToDisplay(filteredStores);
    setNonSalesConsumptionStores(filteredStores);
  }, [selectedGroups]);

  return (
    <Container>
      <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}>
        {i18next.t('ADMIN.INGREDIENTS.NON_SALES_CONSUMPTION_MODAL_TEXT')}
      </Text>
      <RowContainer>
        {!!groups.length && (
          <Dropdown
            customStyle={{ position: 'inherit' }}
            height={'68px'}
            iconSrc={'/images/inpulse/store-black-small.svg'}
            isUniqueSelection={false}
            items={groups}
            label={i18next.t('GENERAL.GROUPS')}
            selectedItems={selectedGroups}
            isRequired
            onSelectionChange={setSelectedGroups}
          />
        )}
        <Dropdown
          customStyle={{ position: 'inherit' }}
          height={'68px'}
          iconSrc={'/images/inpulse/store-black-small.svg'}
          isUniqueSelection={false}
          items={storesToDisplay}
          label={storeName}
          selectedItems={nonSalesConsumptionStores}
          isRequired
          onSelectionChange={setNonSalesConsumptionStores}
        />
        <Dropdown
          customStyle={{ position: 'inherit' }}
          height={'68px'}
          items={frequencies}
          label={i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_COLUMN_TITLE')}
          placeholder={i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_COLUMN_TITLE')}
          selectedItem={selectedFrequency}
          tooltipText={i18next.t('ADMIN.STORES.CONSUMPTION_QUANTITY_COLUMN_TITLE_TOOLTIP', {
            currency,
          })}
          isRequired
          isSearchDisabled
          isUniqueSelection
          onSelectionChange={({ id }) => setConsumptionFrequency(id)}
        />

        <Input
          label={`${i18next.t('ADMIN.STORES.CONSUMPTION_QUANTITY_COLUMN_TITLE')} ${
            !!ingredientUnit ? ` (${translateUnit(ingredientUnit)})` : ''
          }`}
          placeholder={i18next.t('ADMIN.STORES.CONSUMPTION_QUANTITY_COLUMN_TITLE')}
          tooltipText={i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_COLUMN_TITLE_TOOLTIP', {
            currency,
          })}
          type="number"
          value={consumptionQuantity === null ? '' : consumptionQuantity}
          width={240}
          textBig
          onChange={(e) => {
            const value = get(e.target, 'value');
            const formattedValue = formatTextNumberInputValue(value, null);

            setConsumptionQuantity(
              formattedValue !== null && formattedValue >= 0 ? formattedValue : null,
            );
          }}
        />
      </RowContainer>
    </Container>
  );
};

export const getNonSalesConsumptionModalConfig = ({
  handleNonSalesConsumptionModalSave,
  consumptionFrequency,
  setConsumptionFrequency,
  consumptionQuantity,
  setConsumptionQuantity,
  nonSalesConsumptionStores,
  setNonSalesConsumptionStores,
  associatedStores,
  storeName,
  ingredientUnit,
  currency,
}) => ({
  type: 'action',
  width: '560px',
  height: 'auto',
  icon: '/images/inpulse/gear-ipblack-small.svg',
  title: i18next.t('ADMIN.STORES.MANAGE_NON_SALES_CONSUMPTION_ACTIONS'),
  data: {
    consumptionFrequency,
    setConsumptionFrequency,
    consumptionQuantity,
    setConsumptionQuantity,
    nonSalesConsumptionStores,
    setNonSalesConsumptionStores,
    associatedStores,
    storeName,
    ingredientUnit,
    currency,
  },
  component: ModalContent,
  handleCloseCleanUp: () => {
    setConsumptionFrequency(CONSUMPTION_FREQUENCY.DAILY);
    setConsumptionQuantity(null);
    setNonSalesConsumptionStores(associatedStores);
  },
  actions: [
    GENERIC_MODAL_CANCEL_BUTTON(),
    {
      ...GENERIC_MODAL_SAVE_BUTTON(),
      isDisabled: !nonSalesConsumptionStores.length,
      handleClick: handleNonSalesConsumptionModalSave,
    },
  ],
});
